import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | undefined} */
  #cartAddedMessageTimer;

  /** @type {number | undefined} */
  #cartAddedMessageHideTimer;

  /** @type {number | undefined} */
  #productAddIntentTimer;

  /** @type {boolean} */
  #hasPendingProductAddIntent = false;

  /** @type {boolean} */
  #pendingProductAddShouldNotify = false;

  /** @type {string} */
  #pendingProductAddVariantId = '';

  /** @type {string} */
  #pendingProductAddImageUrl = '';

  /** @type {string} */
  #pendingProductAddImageAlt = '';

  /** @type {number} */
  #productAddIntentExpiresAt = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('submit', this.#markPotentialProductAdd, true);
    document.addEventListener('click', this.#markPotentialProductAdd, true);

    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    const { dialog } = /** @type {Refs} */ (this.refs);

    dialog?.addEventListener('close', this.#handleDialogClose);
    dialog?.addEventListener('cancel', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('submit', this.#markPotentialProductAdd, true);
    document.removeEventListener('click', this.#markPotentialProductAdd, true);

    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    const { dialog } = /** @type {Refs} */ (this.refs);

    dialog?.removeEventListener('close', this.#handleDialogClose);
    dialog?.removeEventListener('cancel', this.#handleDialogClose);

    window.clearTimeout(this.#cartAddedMessageTimer);
    window.clearTimeout(this.#cartAddedMessageHideTimer);
    window.clearTimeout(this.#productAddIntentTimer);
  }

  #handleCartAdd = () => {
    const isProductAddAction =
      this.#hasPendingProductAddIntent && Date.now() <= this.#productAddIntentExpiresAt;

    const shouldNotify = isProductAddAction && this.#pendingProductAddShouldNotify;
    const variantId = this.#pendingProductAddVariantId;
    const imageUrl = this.#pendingProductAddImageUrl;
    const imageAlt = this.#pendingProductAddImageAlt;

    this.#clearProductAddIntent();

    // Horizon may dispatch CartAddEvent for quantity updates/removals.
    // We only react to real add-to-cart actions from outside the cart drawer.
    if (!isProductAddAction) return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    if (shouldNotify) {
      requestAnimationFrame(() => {
        this.#showCartAddedMessage({
          variantId,
          imageUrl,
          imageAlt,
        });
      });
    }
  };

  /**
   * Marks a real product add-to-cart action before Horizon updates the cart.
   * This excludes quantity changes, remove buttons, discount forms, and all interactions inside the cart drawer.
   *
   * @param {Event} event
   */
  #markPotentialProductAdd = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Never treat interactions inside the drawer as "new article added".
    if (target.closest('cart-drawer-component')) return;

    let form = null;

    if (event.type === 'submit') {
      form = target instanceof HTMLFormElement ? target : target.closest('form');
    }

    if (event.type === 'click') {
      const trigger = target.closest('button, input[type="submit"], [role="button"]');
      if (!trigger) return;

      const formId = trigger.getAttribute('form');

      if (formId) {
        form = document.getElementById(formId);
      } else {
        form = trigger.closest('form');
      }
    }

    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || form.action || '';
    const isCartAddForm = action.includes('/cart/add');

    if (!isCartAddForm) return;

    const variantId = this.#getVariantIdFromForm(form);
    const variantAlreadyInCart = variantId
      ? Boolean(this.querySelector(`[data-cart-variant-id="${variantId}"]`))
      : false;

    const productImage = this.#getProductImageFromForm(form);

    this.#hasPendingProductAddIntent = true;
    this.#pendingProductAddShouldNotify = !variantAlreadyInCart;
    this.#pendingProductAddVariantId = variantId;
    this.#pendingProductAddImageUrl = productImage.url;
    this.#pendingProductAddImageAlt = productImage.alt;
    this.#productAddIntentExpiresAt = Date.now() + 8000;

    window.clearTimeout(this.#productAddIntentTimer);
    this.#productAddIntentTimer = window.setTimeout(() => {
      this.#clearProductAddIntent();
    }, 8000);
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {{ url: string, alt: string }}
   */
  #getProductImageFromForm(form) {
    const scope =
      form.closest('[data-product-id], product-form-component, product-information, .product, section') ||
      form;

    const image = scope.querySelector('img[src]');

    if (!(image instanceof HTMLImageElement)) {
      return { url: '', alt: '' };
    }

    return {
      url: image.currentSrc || image.src || '',
      alt: image.alt || '',
    };
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {string} variantId
   * @returns {{ url: string, alt: string }}
   */
  #getCartItemImage(dialog, variantId) {
    if (!variantId) return { url: '', alt: '' };

    const escapedVariantId = this.#escapeCss(variantId);
    const row = dialog.querySelector(`[data-cart-variant-id="${escapedVariantId}"]`);
    const image = row?.querySelector('img[src]');

    if (!(image instanceof HTMLImageElement)) {
      return { url: '', alt: '' };
    }

    return {
      url: image.currentSrc || image.src || '',
      alt: image.alt || '',
    };
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeCss(value) {
    return window.CSS?.escape ? window.CSS.escape(value) : value.replaceAll('"', '\\"');
  }

  #getVariantIdFromForm(form) {
    const input = form.querySelector('[name="id"]');

    if (
      input instanceof HTMLInputElement ||
      input instanceof HTMLSelectElement ||
      input instanceof HTMLTextAreaElement
    ) {
      return input.value.trim();
    }

    return '';
  }

  #clearProductAddIntent() {
    this.#hasPendingProductAddIntent = false;
    this.#pendingProductAddShouldNotify = false;
    this.#pendingProductAddVariantId = '';
    this.#pendingProductAddImageUrl = '';
    this.#pendingProductAddImageAlt = '';
    this.#productAddIntentExpiresAt = 0;

    window.clearTimeout(this.#productAddIntentTimer);
  }

  #handleDialogClose = () => {
    this.#removeCartAddedMessage();
  };

  #removeCartAddedMessage() {
    window.clearTimeout(this.#cartAddedMessageTimer);
    window.clearTimeout(this.#cartAddedMessageHideTimer);

    const { dialog } = /** @type {Refs} */ (this.refs);
    const message = dialog?.querySelector('.cart-added-message');

    if (!message) return;

    message.hidden = true;
    message.classList.remove('is-visible');
    message.classList.remove('is-leaving');
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.#removeCartAddedMessage();
    this.closeDialog();
  }

  /**
   * @param {{ variantId?: string, imageUrl?: string, imageAlt?: string }} [options]
   */
  #showCartAddedMessage(options = {}) {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const target = dialog.querySelector('.cart-drawer__content') || dialog;
    const duration = Number(this.dataset.cartAddedDuration || 4800);

    let message = target.querySelector('.cart-added-message');

    if (!message) {
      message = document.createElement('div');
      message.className = 'cart-added-message';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');

      message.innerHTML = `
        <div class="cart-added-message__inner">
          <span class="cart-added-message__media" aria-hidden="true"></span>

          <div class="cart-added-message__content">
            <p class="cart-added-message__title"></p>
            <p class="cart-added-message__text"></p>
          </div>

          <button class="cart-added-message__close" type="button">
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <span class="cart-added-message__progress" aria-hidden="true"></span>
      `;

      target.prepend(message);

      message.querySelector('.cart-added-message__close')?.addEventListener('click', () => {
        this.#hideCartAddedMessage(message);
      });
    }

    const title = message.querySelector('.cart-added-message__title');
    const text = message.querySelector('.cart-added-message__text');
    const close = message.querySelector('.cart-added-message__close');
    const media = message.querySelector('.cart-added-message__media');

    if (title) title.textContent = this.dataset.cartAddedTitle || '';
    if (text) text.textContent = this.dataset.cartAddedText || '';
    if (close) close.setAttribute('aria-label', this.dataset.cartAddedCloseLabel || '');

    const cartItemImage = this.#getCartItemImage(dialog, options.variantId || '');
    const imageUrl = cartItemImage.url || options.imageUrl || '';
    const imageAlt = cartItemImage.alt || options.imageAlt || '';

    if (media instanceof HTMLElement) {
      media.replaceChildren();

      if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'cart-added-message__image';
        image.src = imageUrl;
        image.alt = imageAlt;
        image.loading = 'eager';

        media.hidden = false;
        media.append(image);
      } else {
        media.hidden = true;
      }
    }

    message.hidden = false;
    message.style.setProperty('--cart-added-message-duration', `${duration}ms`);

    message.classList.remove('is-leaving');
    message.classList.remove('is-visible');

    // Restart CSS animation on repeated valid add-to-cart.
    void message.offsetWidth;

    message.classList.add('is-visible');

    window.clearTimeout(this.#cartAddedMessageTimer);
    window.clearTimeout(this.#cartAddedMessageHideTimer);

    this.#cartAddedMessageTimer = window.setTimeout(() => {
      this.#hideCartAddedMessage(message);
    }, duration);
  }

  /**
   * @param {Element} message
   */
  #hideCartAddedMessage(message) {
    window.clearTimeout(this.#cartAddedMessageTimer);

    message.classList.remove('is-visible');
    message.classList.add('is-leaving');

    this.#cartAddedMessageHideTimer = window.setTimeout(() => {
      message.hidden = true;
      message.classList.remove('is-leaving');
    }, 220);
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  #moveInitialFocusToDialog() {
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!(dialog instanceof HTMLDialogElement)) return;

    dialog.setAttribute('tabindex', '-1');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!dialog.open) return;

        dialog.focus({ preventScroll: true });
      });
    });
  }

  #handleDialogOpen = () => {
    this.#updateStickyState();
    this.#moveInitialFocusToDialog();
  };

  #markConfirmedProductAddIntent = (event) => {
    const detail = event.detail || {};

    this.#hasPendingProductAddIntent = true;
    this.#pendingProductAddShouldNotify = true;
    this.#pendingProductAddVariantId = detail.variantId || '';
    this.#pendingProductAddImageUrl = detail.imageUrl || '';
    this.#pendingProductAddImageAlt = detail.imageAlt || '';
    this.#productAddIntentExpiresAt = Date.now() + 8000;

    window.clearTimeout(this.#productAddIntentTimer);

    this.#productAddIntentTimer = window.setTimeout(() => {
      this.#clearProductAddIntent();
    }, 8000);
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}